import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import { message } from 'antd';

// Replaces Polaris React's Frame + Toast. The API mirrors App Bridge's
// shopify.toast.show(), which Polaris web components expect but which needs
// the Shopify admin.
export type ToastOptions = {
  isError?: boolean;
  // Milliseconds, like Polaris Toast and App Bridge.
  duration?: number;
};

export type Toast = {
  show: (content: string, options?: ToastOptions) => void;
};

const DEFAULT_DURATION = 5000;

const ToastContext = createContext<Toast | null>(null);

const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [messageApi, contextHolder] = message.useMessage();

  const toast = useMemo<Toast>(
    () => ({
      show: (content, { isError = false, duration = DEFAULT_DURATION } = {}) => {
        // Keyed by content so a repeated toast (or a StrictMode double effect)
        // replaces the visible one instead of stacking.
        messageApi.open({ key: content, type: isError ? 'error' : 'success', content, duration: duration / 1000 });
      },
    }),
    [messageApi]
  );

  return (
    <ToastContext.Provider value={toast}>
      {contextHolder}
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): Toast => {
  const toast = useContext(ToastContext);
  if (!toast) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return toast;
};

export default ToastProvider;
